import React from 'react';
import PropTypes from 'prop-types';

import {
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_HEADLINE_TEXT,
    BRAND_LIBRARY_BODY_TEXT,
} from 'common/constants/brandLibrary';

import Spacer from 'common/components/Spacer';
import { useNode } from '@splashthat/splash-cms-engine';

import { SessionText } from 'common/components/SessionCard/SessionCard.styles';
import { formatDateTime } from 'common/components/SessionCard/utils';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { useAttendeeSessions } from 'common/components/TouchpointEditor/contexts/AttendeeSessionContext';

import { LeftAlignedSessionTextContainer } from './SessionInformation.styles';

export const PLACEHOLDER_DATE = `Month 00, ${new Date().getFullYear()} | 00:00am - 00:00am`;

export default function SessionInformation({ descendants: { title, date, description }, selected, hovered }) {
    const node = useNode();
    const {
        theme: { colors, activeFonts },
    } = useTouchpointData();
    const { activeSession } = useAttendeeSessions();
    const formattedDate = activeSession.start
        ? formatDateTime(activeSession.start, activeSession.end)
        : PLACEHOLDER_DATE;

    return (
        <LeftAlignedSessionTextContainer
            selected={selected}
            hovered={hovered}
            onClick={node.actions.onClick()}
            onMouseLeave={node.actions.onMouseLeave()}
            onMouseMove={node.actions.onMouseMove()}
        >
            <SessionText
                $fontSize={title.styles.fontSize}
                $fontColor={colors[title.styles.fontColor]}
                $fontFamily={activeFonts[title.styles.fontFamily]}
                data-qa="session_info_title"
            >
                {activeSession.name}
            </SessionText>
            <Spacer $spacing="1rem" />
            <SessionText
                $fontSize={date.styles.fontSize}
                $fontColor={colors[date.styles.fontColor]}
                $fontFamily={activeFonts[date.styles.fontFamily]}
                data-qa="session_info_date"
            >
                {formattedDate}
            </SessionText>
            <Spacer $spacing={{ default: '2.5rem', medium: '1.5rem' }} />
            <SessionText
                $fontSize={description.styles.fontSize}
                $fontColor={colors[description.styles.fontColor]}
                $fontFamily={activeFonts[description.styles.fontFamily]}
                data-qa="session_info_description"
            >
                {activeSession.description}
            </SessionText>
        </LeftAlignedSessionTextContainer>
    );
}

SessionInformation.splash = {
    related: {
        settings: React.lazy(() => import('./SessionInformation.tools')),
    },
    defaultProps: {
        descendants: {
            title: {
                styles: {
                    fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    fontSize: 24,
                    fontFamily: BRAND_LIBRARY_HEADLINE_TEXT,
                },
            },
            date: {
                styles: {
                    fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    fontSize: 16,
                    fontFamily: BRAND_LIBRARY_BODY_TEXT,
                },
            },
            description: {
                styles: {
                    fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    fontSize: 16,
                    fontFamily: BRAND_LIBRARY_BODY_TEXT,
                },
            },
        },
    },
};

SessionInformation.propTypes = {
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    descendants: PropTypes.shape({
        title: PropTypes.shape({
            styles: PropTypes.shape({
                fontColor: PropTypes.number.isRequired,
                fontSize: PropTypes.number.isRequired,
                fontFamily: PropTypes.number.isRequired,
            }),
        }),
        date: PropTypes.shape({
            styles: PropTypes.shape({
                fontColor: PropTypes.number.isRequired,
                fontSize: PropTypes.number.isRequired,
                fontFamily: PropTypes.number.isRequired,
            }),
        }),
        description: PropTypes.shape({
            styles: PropTypes.shape({
                fontColor: PropTypes.number.isRequired,
                fontSize: PropTypes.number.isRequired,
                fontFamily: PropTypes.number.isRequired,
            }),
        }),
    }).isRequired,
};
